import React from 'react';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ASSET_MAX_LENGTH } from '../manifest/valueUtils';

interface ResolveAssetFieldProps {
  testId: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /** Set when the URL breaks a backend rule; the message itself renders under the row. */
  error?: boolean;
}

/**
 * The `asset` control (currently only `logo_image`) — an image preview beside a URL text
 * field. There is no shared file/URL-picker component in `Components/DesignSystem/` yet
 * (`FileUpload` delivers `File` objects from a dropzone, which is the wrong shape for a
 * setting whose manifest `type` is a plain string URL) — this reproduces exactly what
 * `BrandingThemeSection.tsx` hand-wired for `logo_image` before the generic renderer.
 */
const ResolveAssetField: React.FC<ResolveAssetFieldProps> = ({ testId, value, onChange, disabled, error = false }) => (
  <div className="tw-flex tw-items-center tw-gap-2">
    <div
      className="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-h-9 tw-w-9 tw-rounded-ctl tw-border tw-border-edge tw-bg-surface-sunken tw-overflow-hidden"
      data-testid={`${testId}-preview`}
    >
      {value ? (
        // eslint-disable-next-line jsx-a11y/img-redundant-alt
        <img src={value} alt="Logo image preview" className="tw-h-full tw-w-full tw-object-contain" />
      ) : (
        <Typography variant="caption-metadata" className="tw-text-ink-tertiary">
          none
        </Typography>
      )}
    </div>
    <TextField
      label=""
      placeholder="Image URL"
      value={value}
      // Surrounding spaces are invisible, and the backend rejects a URL that has them.
      onChange={(next: string) => onChange(next.trim())}
      maxLength={ASSET_MAX_LENGTH}
      error={error}
      disabled={disabled}
      className="tw-flex-1"
      data-testid={testId}
    />
  </div>
);

export default ResolveAssetField;
